import {HStack, Show, Text, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import type {EvaluateConfigurationResponseBody, PriceBasis} from '@dms/api/new-vehicle';
import {BasisPrice, MoneyText} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';

import {newVehicleLabel} from '../utils/newVehicleLabels';

type Evaluation = EvaluateConfigurationResponseBody;
type Item = Evaluation['items'][number];

type Props = {
  basis: PriceBasis;
  before: Evaluation;
  after: Evaluation;
  currency: string;
  names: Map<string, string>;
  canViewCost: boolean;
};

const t = (key: string, fallback: string) => i18n.t(key, {defaultValue: fallback});
const itemKey = (item: Item) => `${item.type}:${item.id}`;

function ItemValue(props: {item: Item; currency: string; basis: PriceBasis}) {
  const price =
    isNotNil(props.item.priceNet) && isNotNil(props.item.priceGross) ? (
      <BasisPrice
        net={props.item.priceNet}
        gross={props.item.priceGross}
        basis={props.basis}
        currency={props.currency}
      />
    ) : (
      <ItemPriceFallback item={props.item} currency={props.currency} basis={props.basis} />
    );
  return (
    <VStack spacing={1}>
      <Text>{newVehicleLabel('availability', props.item.availability)}</Text>
      {price}
    </VStack>
  );
}

function ItemPriceFallback(props: {item: Item; currency: string; basis: PriceBasis}) {
  return isNotNil(props.item.priceNet) ? (
    <HStack spacing={1} align="baseline">
      <MoneyText amount={props.item.priceNet} currency={props.currency} />
      <Show when={props.basis === 'gross'}>
        <Text size="xSmall" color="secondary">
          {t('entity.newVehicle.labels.amountNet', 'net')}
        </Text>
      </Show>
    </HStack>
  ) : (
    <Text>—</Text>
  );
}

export function ConflictReview(props: Props) {
  const beforeItems = new Map(props.before.items.map((item) => [itemKey(item), item]));
  const afterItems = new Map(props.after.items.map((item) => [itemKey(item), item]));
  const changedKeys = new Set(
    [...beforeItems.keys(), ...afterItems.keys()].filter((key) => {
      const oldItem = beforeItems.get(key);
      const newItem = afterItems.get(key);
      return (
        !oldItem ||
        !newItem ||
        oldItem.availability !== newItem.availability ||
        oldItem.priceNet !== newItem.priceNet
      );
    })
  );
  const totals: Array<[keyof Evaluation['totals'], string]> = [
    ['net', t('entity.newVehicle.labels.net', 'Net')],
    ['vat', t('general.labels.vat', 'VAT')],
    ['gross', t('entity.newVehicle.labels.gross', 'Gross')],
    ['bundleSavingsNet', t('entity.newVehicle.labels.bundleSavings', 'Bundle savings')],
  ];
  if (props.canViewCost) {
    totals.push(['costNet', t('entity.newVehicle.labels.totalCost', 'Total cost')]);
  }

  return (
    <VStack spacing={3}>
      <Text>
        {t(
          'entity.newVehicle.notifications.catalogueChanged',
          'The catalogue changed after this price was saved'
        )}
      </Text>
      {[...changedKeys].map((key) => {
        const oldItem = beforeItems.get(key);
        const newItem = afterItems.get(key);
        const item = newItem ?? oldItem;
        if (!item) {
          return null;
        }
        return (
          <VStack key={key} spacing={1}>
            <Text>{props.names.get(item.id) ?? newVehicleLabel('itemType', item.type)}</Text>
            {/* eslint-disable-next-line no-nested-ternary -- Pre-existing violation */}
            {!oldItem ? (
              <>
                <Text>{t('entity.newVehicle.labels.added', 'Added')}</Text>
                <Show when={newItem}>
                  {(newItem) => (
                    <ItemValue item={newItem} currency={props.currency} basis={props.basis} />
                  )}
                </Show>
              </>
            ) : !newItem ? (
              <>
                <Text>{t('entity.newVehicle.labels.removed', 'Removed')}</Text>
                <ItemValue item={oldItem} currency={props.currency} basis={props.basis} />
              </>
            ) : (
              <>
                <ItemValue item={oldItem} currency={props.currency} basis={props.basis} />
                <Text>→</Text>
                <ItemValue item={newItem} currency={props.currency} basis={props.basis} />
              </>
            )}
          </VStack>
        );
      })}
      {totals.map(([key, title]) => (
        <VStack key={key} spacing={1}>
          <Text>{title}</Text>
          <MoneyText amount={props.before.totals[key] ?? '—'} currency={props.currency} />
          <Text>→</Text>
          <MoneyText amount={props.after.totals[key] ?? '—'} currency={props.currency} />
        </VStack>
      ))}
    </VStack>
  );
}
